import { signal } from "@preact/signals";
import { route } from "preact-router";
import { fetchSelfUser, fetchLoginRequest, fetchLogoutAllDevices, tryRefreshToken, UserProfile } from "services/backend";
import { uiAlert, uiToast } from "services/uiservice";

interface _UserState {
    status: "pending" | "none" | "loggedin";
    user: UserProfile | null;
    accessToken: string | null;
    refreshToken: string | null;
    rememberMe: boolean;
}

export const globalUserState = signal<_UserState>({
    status: (localStorage.getItem("refreshtoken") ?? sessionStorage.getItem("refreshtoken")) ? "pending" : "none",
    user: null,
    accessToken: localStorage.getItem("accesstoken") ?? sessionStorage.getItem("accesstoken"),
    refreshToken: localStorage.getItem("refreshtoken") ?? sessionStorage.getItem("refreshtoken"),
    rememberMe: !!localStorage.getItem("rememberme")
});

globalUserState.subscribe(state => {
    if (state.rememberMe) {
        localStorage.setItem("rememberme", "true");
    } else {
        localStorage.removeItem("rememberme");
    }

    if (state.refreshToken) {
        if (state.rememberMe) {
            localStorage.setItem("refreshtoken", state.refreshToken);
            sessionStorage.removeItem("refreshtoken");
        } else {
            sessionStorage.setItem("refreshtoken", state.refreshToken);
            localStorage.removeItem("refreshtoken");
        }
    } else {
        sessionStorage.removeItem("refreshtoken");
        localStorage.removeItem("refreshtoken");
    }

    if (state.accessToken) {
        if (state.rememberMe) {
            localStorage.setItem("accesstoken", state.accessToken);
            sessionStorage.removeItem("accesstoken");
        } else {
            sessionStorage.setItem("accesstoken", state.accessToken);
            localStorage.removeItem("accesstoken");
        }
    } else {
        sessionStorage.removeItem("accesstoken");
        localStorage.removeItem("accesstoken");
    }

    updateLoginState();
});

export async function initUserState() {
    window.addEventListener("storage", (event) => {
        console.log("storage listener", event);
        if (event.key == "accesstoken " || event.key == "refreshtoken") {
            // todo: react if other tabs update the tokens in storage
        }
    });

    if (globalUserState.peek().refreshToken && !globalUserState.peek().accessToken) {
        await tryRefreshToken();
    }
    await _fetchAndSetUserInState();
}

async function _fetchAndSetUserInState() {
    const user = globalUserState.peek().accessToken ? (await fetchSelfUser()) : null;
    if (user) {
        globalUserState.value = {
            ...globalUserState.value,
            status: "loggedin",
            user: user,
        };
    } else {
        logout();
    }
}

export async function login(username: string, password: string): Promise<boolean> {
    const result = await fetchLoginRequest(username, password);
    if (result?.data?.token?.accessToken) {
        globalUserState.value = {
            ...globalUserState.value,
            status: "pending"
        };
        setTokens(result.data.token.accessToken, result.data.token.refreshToken ?? undefined);
        await _fetchAndSetUserInState();
        globalUserState.value = {
            ...globalUserState.value,
            status: "loggedin"
        };
        return true;
    }
    return false;
}

export async function logout() {
    removeTokens();
    globalUserState.value = {
        ...globalUserState.value,
        accessToken: null,
        refreshToken: null,
        user: null,
        status: "none"
    };
    route("/");
}

export async function logoutAllDevices() {
    const logoutResult = await fetchLogoutAllDevices();
    if (logoutResult?.data?.user?.logoutAllDevices?.ok) {
        removeTokens();
        await uiAlert("It may take up to 30 minutes for all devices to be logged out.");
        logout();
    } else {
        uiToast("Error logging out", "error");
    }
}



function removeTokens() {
    localStorage.removeItem("accesstoken");
    sessionStorage.removeItem("accesstoken");
    localStorage.removeItem("refreshtoken");
    sessionStorage.removeItem("refreshtoken");
}

export function setTokens(accessToken: string, refreshToken: string | undefined) {
    globalUserState.value = {
        ...globalUserState.value,
        accessToken,
        refreshToken: refreshToken ?? globalUserState.value.refreshToken ?? null,
    };
}

export function updateLoginState() {
    window.dispatchEvent(new Event("storage"));
}
